import { NavLink, Outlet, useNavigate, Link } from 'react-router-dom';
import { LayoutDashboard, Home, ClipboardList, Award, Settings, LogOut, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { usePortalAuthStore } from '@/store/portal-auth.store';
import { usePortalMe } from '@/api/hooks/usePortal';
import { cn, getInitials } from '@/lib/utils';

const NAV_ITEMS = [
  { to: '/portal/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/portal/home', label: 'Relationships', icon: Home },
  { to: '/portal/assessments', label: 'Assessments', icon: ClipboardList },
  { to: '/portal/certifications', label: 'Certifications', icon: Award },
  { to: '/portal/settings', label: 'Settings', icon: Settings },
];

export function PortalLayout() {
  const { logout } = usePortalAuthStore();
  const navigate = useNavigate();
  const { data: me } = usePortalMe();

  function handleLogout() {
    logout();
    navigate('/portal/login');
  }

  const displayName = me
    ? [me.first_name, me.last_name].filter(Boolean).join(' ') || me.email
    : null;

  return (
    <div className="min-h-screen bg-bg-base">
      {/* Top nav */}
      <header className="sticky top-0 z-40 border-b border-border-subtle bg-bg-surface/90 backdrop-blur-sm">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          {/* Logo + company — clicks to /portal/home */}
          <Link to="/portal/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img src="/verigrc-shield-256.png" alt="VeriGRC" className="h-7 w-7 shrink-0 object-contain" />
            <span className="text-sm font-semibold text-text-primary hidden sm:block">
              Vendor Portal
            </span>
          </Link>

          {/* Nav links */}
          <nav className="flex items-center gap-1">
            {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors',
                    isActive
                      ? 'bg-bg-surface-2 text-text-primary font-medium'
                      : 'text-text-secondary hover:text-text-primary hover:bg-bg-surface-2',
                  )
                }
              >
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{label}</span>
              </NavLink>
            ))}
          </nav>

          {/* User identity → name-click dropdown (Sign out only) */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Account menu"
                className="flex items-center gap-2.5 rounded-lg border border-border-subtle bg-bg-surface-2 px-2 py-1.5 transition-colors hover:bg-bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-cyan focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base"
              >
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-gradient-to-br from-accent-cyan-dim to-bg-surface-2 text-[11px] font-bold uppercase text-accent-sky ring-1 ring-accent-cyan/25">
                  {me ? getInitials(displayName ?? me.email) : '?'}
                </span>
                {me && (
                  <span className="hidden text-left leading-tight md:block">
                    {displayName && displayName !== me.email && (
                      <span className="block max-w-[150px] truncate text-xs font-medium text-text-primary">{displayName}</span>
                    )}
                    <span className="block max-w-[150px] truncate text-xs text-text-secondary">{me.email}</span>
                  </span>
                )}
                <ChevronDown className="h-4 w-4 shrink-0 text-text-secondary" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {me && (
                <>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-0.5">
                      {displayName && displayName !== me.email && (
                        <p className="truncate text-sm font-medium text-text-primary">{displayName}</p>
                      )}
                      <p className="truncate text-xs text-text-secondary">{me.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem
                className="text-accent-rose focus:text-accent-rose"
                onSelect={handleLogout}
              >
                <LogOut />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Content */}
      <main className="mx-auto max-w-6xl px-4 py-6">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-border-subtle py-4 text-center text-xs text-text-secondary">
        Powered by <span className="font-medium text-text-primary">VeriGRC</span>
      </footer>
    </div>
  );
}
